/**
 * Manual order that already exists: find it, recover it, open it.
 *
 * Staff add an order by hand when the POS never sent it. The backend answers 400
 * "Order with delivery_id X already exists." when the order is already there. The usual reason
 * is that the tablet missed it (a deaf Events socket: the order is in the database, the tablet
 * never heard about it). Showing a generic "try again" then sends the operator round in circles;
 * the right answer is to find the order, repairing the tablet's own state on the way, and open
 * it.
 *
 * The decision flow is here, with its dependencies injected, so it is unit-testable without
 * mounting App.vue. App.vue wires the real lookups, the socket recovery and the wide fetch.
 *
 * A parallel backend change ADDS to the 400 body `code: "ORDER_ALREADY_EXISTS"`, `order_id`,
 * `created_at` and `has_photo` (status and message unchanged). Both the old and the new body are
 * handled: the code wins when present, the message is the fallback.
 */

export const ORDER_ALREADY_EXISTS = 'ORDER_ALREADY_EXISTS';

const norm = (value) => (value == null ? '' : String(value).trim().toLowerCase());

/**
 * Does this failed POST /create-order mean "the order already exists"?
 *
 * @param {{status: number|null, body: object|string|null, code: string|null}} parsed
 *   from `parseRestError` (restApiFallback.js)
 * @param {string} [fallbackText] the thrown error's own message, used when the body carries none
 * @returns {{code: string|null, orderId: string|null, createdAt: string|null, hasPhoto: boolean|null, status: number|null}|null}
 */
export function orderAlreadyExists(parsed, fallbackText = '') {
  if (!parsed) return null;
  const { status, body, code } = parsed;
  if (code === ORDER_ALREADY_EXISTS) {
    return {
      code,
      orderId: body?.order_id ?? null,
      createdAt: body?.created_at ?? null,
      hasPhoto: typeof body?.has_photo === 'boolean' ? body.has_photo : null,
      status,
    };
  }
  // Old backend: status 400 and the message. A different status with the same words is not ours.
  if (status != null && status !== 400) return null;
  const text = typeof body === 'string'
    ? body
    : [body?.message, body?.error, body?.detail].filter((part) => typeof part === 'string').join(' ') || fallbackText || '';
  if (!/already exists/i.test(text)) return null;
  return { code: null, orderId: null, createdAt: null, hasPhoto: null, status };
}

/** The list shows orders created in the last 60 minutes (the backend's default window). */
export const LIST_WINDOW_MS = 60 * 60 * 1000;

/**
 * Look the typed order up among the orders several lists hold.
 *
 * A hit is a match ONLY when the platform AND the delivery id both equal what was typed
 * (case-insensitive, trimmed), including when the backend sent an `order_id`. The backend's
 * duplicate check ignores the platform (a parallel change makes it platform-aware, but old Lambdas
 * may still be deployed), so "Deliveroo 1234" can be refused because "ECOMMERCE 1234" exists:
 * opening that other order would put the operator's photos on the wrong bag.
 *
 *  - 'match':     exactly one order has that platform and delivery id (the `order_id` breaks a tie).
 *  - 'ambiguous': several different orders have that platform and delivery id and nothing says
 *                 which one the backend meant: refuse rather than take the first.
 *  - 'conflict':  the code exists, but only under other platforms (`platforms`): say so.
 *  - 'none':      no order with that code at all.
 *
 * @returns {{status: 'match'|'ambiguous'|'conflict'|'none', order: object|null, platforms: string[]}}
 */
export function lookupDuplicate(lists, { platform, deliveryId, orderId = null }) {
  const wantedDelivery = norm(deliveryId);
  const wantedPlatform = norm(platform);
  const none = { status: 'none', order: null, platforms: [] };
  if (!wantedDelivery) return none;

  // Distinct orders carrying the typed code, whichever list they came from (the same order is
  // usually in several: what is on screen, processedOrders, rawOrders).
  const sameCode = [];
  const seen = new Set();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const order of list) {
      if (!order || norm(order.deliveryId) !== wantedDelivery) continue;
      const key = order.orderId ?? order;
      if (seen.has(key)) continue;
      seen.add(key);
      sameCode.push(order);
    }
  }
  if (sameCode.length === 0) return none;

  const exact = wantedPlatform ? sameCode.filter((order) => norm(order.deliveryPlatform) === wantedPlatform) : [];
  if (orderId) {
    const byId = exact.find((order) => order.orderId === orderId);
    if (byId) return { status: 'match', order: byId, platforms: [] };
  }
  if (exact.length === 1) return { status: 'match', order: exact[0], platforms: [] };
  if (exact.length > 1) return { status: 'ambiguous', order: null, platforms: [] };

  // The code exists, but not under the typed platform. The order the backend named (when it sent
  // one) is the one to report; otherwise every platform that holds the code.
  const named = orderId ? sameCode.filter((order) => order.orderId === orderId) : [];
  const holders = (named.length ? named : sameCode).map((order) => order.deliveryPlatform).filter(Boolean);
  return { status: 'conflict', order: null, platforms: [...new Set(holders)] };
}

/**
 * An `order(id:)` result (the Order type) as the row `getRawOrdersRealtime` would have returned
 * for it, so `toProcessedOrder` treats both alike. Fields only the realtime mirror carries
 * (brand, processedAt, recipient) are null: the tablet only needs them for the list.
 */
export function rawRowFromOrder(order) {
  if (!order || !order.id) return null;
  return {
    id: order.id,
    posId: order.posProviderId ?? null,
    deliveryId: order.deliveryId ?? null,
    processedAt: null,
    createdAt: order.createdAt ?? null,
    ticketId: order.ticketId ?? null,
    brand: null,
    brandByBrand: null,
    deliveryPlatform: order.deliveryPlatform ?? null,
    recipientName: null,
    recipientAddress: null,
    orderId: order.id,
    order: {
      hasPhoto: order.hasPhoto ?? false,
      manualIsCorrect: order.manualIsCorrect ?? false,
      photos: order.photos || [],
      orderAnalyses: order.orderAnalyses || [],
      allergies: order.allergies ?? null,
      comment: order.comment ?? null,
      orderDigitalTicket: order.orderDigitalTicket ?? null,
      orderEvents: order.orderEvents || [],
    },
  };
}

/**
 * The ordered steps, stopping at the first that settles it:
 *  1. it is already on the tablet: open it, no recovery (nothing was missed);
 *  2. it is not: rebuild the socket once (bypassing the backoff), refetch the orders, look again.
 *     Skipped when the backend says the order is older than the list's 60 minutes: such an order
 *     is not in the list whatever the socket did, so the rebuild would only cost requests;
 *  3. still not: one-off wide lookup (the 60 min window does not apply), opened as a transient
 *     selection even when older than the list shows;
 *  4. nowhere: say so specifically.
 * A platform mismatch or an ambiguous code ends the flow at any step: the order must not be
 * guessed.
 *
 * @param {object} deps
 * @param {object} deps.target  `{platform, deliveryId, orderId?, createdAt?}`
 * @param {() => {status: string, order: object|null, platforms: string[]}} deps.findLocal
 *        `lookupDuplicate` over what the tablet holds NOW
 * @param {() => Promise<void>} deps.recoverSocket
 * @param {() => Promise<void>} deps.refreshOrders  await a full order fetch
 * @param {() => Promise<{status: string, order: object|null, platforms: string[]}>} deps.fetchWide
 *        the one-off lookup, same result shape, `order` already processed
 * @param {(event: string, props?: object) => void} deps.report  forensic event, without `fx_`
 * @param {number} [deps.now]
 * @returns {Promise<{outcome: 'visible'|'recovered'|'wide'|'platform_mismatch'|'ambiguous'|'not_found',
 *   order: object|null, transient: boolean, platforms: string[]}>}
 */
export async function resolveManualDuplicate({ target, findLocal, recoverSocket, refreshOrders, fetchWide, report, now = Date.now() }) {
  const base = {
    platform: target.platform ?? null,
    delivery_id: target.deliveryId ?? null,
    order_id: target.orderId ?? null,
  };
  const stop = (found, outcome) => {
    if (found.status === 'conflict') {
      report('manual_duplicate_platform_mismatch', { ...base, held_by: found.platforms.join(',') });
      return { outcome: 'platform_mismatch', order: null, transient: false, platforms: found.platforms };
    }
    if (found.status === 'ambiguous') {
      report('manual_duplicate_ambiguous', base);
      return { outcome: 'ambiguous', order: null, transient: false, platforms: [] };
    }
    if (found.status === 'match') {
      report(`manual_duplicate_${outcome}`, { ...base, order_id: found.order.orderId ?? base.order_id });
      return { outcome, order: found.order, transient: outcome === 'wide', platforms: [] };
    }
    return null;
  };

  let found = findLocal();
  const first = stop(found, 'visible');
  if (first) return first;

  const created = target.createdAt ? Date.parse(target.createdAt) : NaN;
  const olderThanList = Number.isFinite(created) && now - created > LIST_WINDOW_MS;

  let recoveryFailed = false;
  if (!olderThanList) {
    try {
      await recoverSocket();
      await refreshOrders();
    } catch (_) {
      // Best effort: the wide lookup below does not depend on the socket.
      recoveryFailed = true;
    }
    found = findLocal();
    const second = stop(found, 'recovered');
    if (second) return second;
  }

  let fetchFailed = false;
  try {
    found = await fetchWide();
  } catch (_) {
    fetchFailed = true;
    found = { status: 'none', order: null, platforms: [] };
  }
  const third = stop(found, 'wide');
  if (third) return third;

  report('manual_duplicate_not_found', {
    ...base, fetch_failed: fetchFailed, recovery_failed: recoveryFailed, recovery_skipped: olderThanList,
  });
  return { outcome: 'not_found', order: null, transient: false, platforms: [] };
}
